import React from 'react';

import { EditOutlined, CheckOutlined, CloseOutlined, LinkOutlined } from '@ant-design/icons';
import Can from '@shared/permissions/Can';
import ListPageHeader from '@shared/ListPageHeader';
import { Card, Descriptions, Button, Empty, Tag, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

interface InventoryType {
  id: string;
  name: string;
}

interface ProductRef {
  id: string;
  name: string;
  sku?: string;
}

interface BundleProduct {
  id: string;
  product?: ProductRef;
  quantity: number;
  inventory_type?: InventoryType | null;
}

interface Bundle {
  id: string;
  name: string;
  sku?: string;
  barcode?: string;
  activated?: boolean;
  bundle_products?: BundleProduct[];
}

interface BundleShowViewProps {
  bundle: Bundle | null | undefined;
  role?: 'admin' | 'operator';
}

/**
 * BundleShowView - 通用組合包詳情組件
 *
 * @param {Object} props
 * @param {Object} props.bundle - 組合包資料（由 loader 預載）
 * @param {string} props.role - 角色 (admin 或 operator)
 */
const BundleShowView: React.FC<BundleShowViewProps> = ({
  bundle,
  role: _role = 'admin',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`edit`);
  };

  if (!bundle) {
    return (
      <div>
        <Empty description={t('common.error.no_data')} />
      </div>
    );
  }
  const headerBasePath = _role === 'operator' ? '/operator' : '/admin';

  // 組合包產品表格欄位（ProductCell 單欄堆疊 + 量 + 庫存類型）
  const columns = [
    {
      title: t(`models.product.name`),
      key: 'product',
      render: (_: unknown, record: BundleProduct) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div>
              <Link to={`/products/${record.product?.id}`} style={{ wordBreak: 'break-word' }}>
                {record.product?.name}
              </Link>
            </div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.product?.sku}</div>
          </div>
          <a
            href={`${headerBasePath}/products/${record.product?.id}`}
            target="_blank"
            rel="noreferrer"
            style={{ flexShrink: 0 }}
          >
            <LinkOutlined style={{ fontSize: 12, color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      title: t(`models.bundle_product.quantity`),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (quantity: number) => <Tag color="blue">{quantity}</Tag>,
    },
    {
      title: t(`models.bundle_product.inventory_type`),
      dataIndex: 'inventory_type',
      key: 'inventory_type',
      width: 180,
      render: (inventory_type: InventoryType | null | undefined) => (
        inventory_type ? (
          <Tag color="blue">{inventory_type.name}</Tag>
        ) : (
          <Tag color="default">{t(`shared.bundles.form.default_inventory_type`)}</Tag>
        )
      ),
    },
  ];

  const headerActions = (
    <Can requiredPermission="products:write">
      <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
        {t('common.button.edit')}
      </Button>
    </Can>
  );

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['products', 'products.bundles', { label: bundle.name }]}
        actions={headerActions}
      />
      {/* 基本資訊 */}
      <Descriptions column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t(`models.bundle.name`)}>
          {bundle.name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.bundle.sku`)}>
          {bundle.sku || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.bundle.barcode`)}>
          {bundle.barcode || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t(`models.bundle.activated`)}>
          {bundle.activated ? (
            <CheckOutlined color="green"/>
          ) : (
            <CloseOutlined color="gray"/>
          )}
        </Descriptions.Item>
      </Descriptions>
      {/* 組合包產品 */}
      <Card title={t(`models.bundle.bundle_items`)}>
        {bundle.bundle_products && bundle.bundle_products.length > 0 ? (
          <Table
            columns={columns}
            dataSource={bundle.bundle_products}
            rowKey="id"
            pagination={false}
          />
        ) : (
          <Empty description={t(`shared.bundles.detail.no_products`)} />
        )}
      </Card>
    </div>
  );
};

export default BundleShowView;
